import type {AdTagConfig, PlayerEventBase, UserInteractionEvent} from 'bitmovin-player';

import {isNotNullish} from '../../utils/Utils';
import {AdCallbacks, AdModuleAPI} from '../internal/ads/AdModuleAPI';

import {mapBitmovinAd, mapBitmovinAdBreak, mapBitmovinAdError} from './ads/AdUtils';
import type {Bitmovin8PlayerProxy} from './Bitmovin8PlayerProxy';
import {BITMOVIN8_PLAYER_EVENT} from './player/Bitmovin8PlayerTypes';

export class Bitmovin8AdModule implements AdModuleAPI {
  adCallbacks = new AdCallbacks();

  private readonly player: Bitmovin8PlayerProxy;

  constructor(player: Bitmovin8PlayerProxy) {
    this.player = player;
  }

  isLinearAdActive = () => this.player.isLinearAdActive() ?? false;

  getContainer = () => this.player.getContainer();

  getAdModuleInfo = () => this.player.getAdModuleInfo() ?? {name: undefined, version: undefined};

  currentTime = () => this.player.getCurrentTime() ?? 0;

  getAutoPlay(): boolean {
    return this.player.autoplay ?? false;
  }

  registerEventListeners(): void {
    this.player.on(BITMOVIN8_PLAYER_EVENT.Play, (e: UserInteractionEvent) => {
      if (e.issuer === 'advertising-api') {
        this.adCallbacks.onPlay();
      }
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.Paused, (e: UserInteractionEvent) => {
      if (e.issuer === 'advertising-api') {
        this.adCallbacks.onPause();
      }
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdStarted, (event: any) => {
      this.adCallbacks.onAdStarted(mapBitmovinAd(event.ad));
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdFinished, (event: any) => {
      this.adCallbacks.onAdFinished(mapBitmovinAd(event.ad));
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdBreakStarted, (event: any) => {
      this.adCallbacks.onAdBreakStarted(mapBitmovinAdBreak(event.adBreak));
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdBreakFinished, (event: any) => {
      this.adCallbacks.onAdBreakFinished(mapBitmovinAdBreak(event.adBreak));
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdClicked, (event: any) => {
      this.adCallbacks.onAdClicked({clickThroughUrl: event.clickThroughUrl});
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdQuartile, (event: any) => {
      this.adCallbacks.onAdQuartile({quartile: event.quartile});
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdSkipped, (event: any) => {
      this.adCallbacks.onAdSkipped(mapBitmovinAd(event.ad));
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdError, (event: PlayerEventBase) => {
      this.adCallbacks.onAdError(mapBitmovinAdError(event, this.player.getActiveAd()?.id));
    });
    this.player.on(BITMOVIN8_PLAYER_EVENT.AdManifestLoaded, (event: any) => {
      const adTagConfig = event.adConfig as AdTagConfig;
      const isVmap = adTagConfig?.tag?.type === 'vmap';
      const adBreakInfo = isNotNullish(event.adConfig) ? mapBitmovinAdBreak(event.adConfig) : undefined;
      const manifestDownloadTime = event.downloadTiming?.downloadTime;
      this.adCallbacks.onAdManifestLoaded({
        adBreakInfo,
        manifestDownloadTime,
        isVmap,
      });
    });
  }
}
